import { useMutation, useQueryClient } from '@tanstack/react-query';
import { SearchSelectField } from '@/components/ui/SearchSelectField/SearchSelectField';
import { api } from '@/api/client';
import type { Edition } from '@/api/client';
import { useCanManageCatalog } from '@/contexts/CatalogManageOpenerContext';

const PAGE_SIZE = 25;
const MIN_SEARCH_LENGTH = 2;

export type EditionValueItem = {
  id: string;
  name: string;
  department_id?: string | null;
  department_code?: string | null;
};

interface EditionComboProps {
  value: EditionValueItem | null;
  onChange: (v: EditionValueItem | null) => void;
  departmentId: string | null;
  placeholder?: string;
  disabled?: boolean;
  containerRef?: React.RefObject<HTMLDivElement | null>;
  onCreateError?: (err: Error) => void;
  debounceFlushKey?: string | number;
}

function toValueItem(e: Edition): EditionValueItem {
  return {
    id: e.id,
    name: e.name,
    department_id: e.department_id,
    department_code: e.department_code,
  };
}

export function EditionCombo({
  value,
  onChange,
  departmentId,
  placeholder = 'Редакция',
  disabled,
  containerRef,
  onCreateError,
  debounceFlushKey,
}: EditionComboProps) {
  const canManageCatalog = useCanManageCatalog();
  const queryClient = useQueryClient();
  const createMutation = useMutation({
    mutationFn: (name: string) => api.editions.create({ name, department_id: departmentId! }),
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: ['editions'] });
      onChange(toValueItem(created));
    },
    onError: (err: Error) => onCreateError?.(err),
  });

  return (
    <SearchSelectField<EditionValueItem>
      value={value}
      onChange={onChange}
      getOptionLabel={(e) => e.name}
      placeholder={placeholder}
      disabled={disabled}
      containerRef={containerRef}
      queryKey={['editions', 'search-field', departmentId ?? '']}
      fetchPage={(debouncedQuery, page) =>
        api.editions.list(debouncedQuery, departmentId, { limit: PAGE_SIZE, offset: page * PAGE_SIZE })
      }
      minSearchLength={MIN_SEARCH_LENGTH}
      debounceFlushKey={debounceFlushKey}
      computeAddons={({ debouncedQuery, filteredItems }) => {
        if (!canManageCatalog) return [];
        const q = debouncedQuery.trim();
        if (!departmentId || !q || createMutation.isPending) return [];
        const exact = filteredItems.some((r) => r.name.toLowerCase() === q.toLowerCase());
        if (exact) return [];
        return [
          {
            id: '__create_edition',
            label: `+ Добавить «${q}»`,
            onSelect: () => createMutation.mutate(q),
          },
        ];
      }}
      addonPending={createMutation.isPending}
    />
  );
}
